import axios from "./axios.interceptors";
import type { UserBooking } from "../types/service-types/UserBookingTypes";

export type UploadedInvoice = {
  id: string;
  name: string;
  url: string; // backend should return a file URL; adjust if you serve via signed URLs
};

export type PaymentReceipt = {
  id: string;
  url: string;
  originalFilename: string;
  uploadedAt: string;
};

export type PaymentReceiptStatus = {
  hasUploaded: boolean;
  canUploadMore: boolean;
  uploadedCount: number;
  receipts: PaymentReceipt[];
};

export type SeatSelectionRequest = {
  pnrCode: string;
  lastName: string;
  departureSeatId?: string | null;
  returnSeatId?: string | null;
};

export type PendingSeatSave = {
  pnrCode: string;
  travelers: Array<{
    travelerId: string;
    lastName: string;
    departureSeatId?: string;
    returnSeatId?: string;
  }>;
};

const RESERVATIONS_BASE = "/api/reservations";
// Adjust these to match your real endpoints if different:
const INVOICES_BASE = "/api/v1/reservations"; // e.g. /{reservationId}/invoices

const getBackendErrorMessage = (err: any) => {
  const data = err?.data ?? err?.response?.data;
  const message = data?.data?.message ?? data?.message ?? err?.message;

  return typeof message === "string" && message.trim()
    ? message
    : "Unknown error";
};

class ReservationsService {
  /**
   * GET /api/v1/reservations/search-by-pnr?lastName={lastName}&pnrCode={pnrCode}
   */
  public async searchByPnr(pnrCode: string, lastName: string): Promise<UserBooking> {
    try {
      const res = await axios.get("/api/v1/reservations/search-by-pnr", {
        params: { lastName, pnrCode },
      });
      return res.data as UserBooking;
    } catch (err) {
      throw new Error(getBackendErrorMessage(err));
    }
  }

  public async selectTravelerSeat(
    travelerId: string,
    request: SeatSelectionRequest
  ): Promise<void> {
    try {
      await axios.put(
        `/api/v1/reservations/travelers/${encodeURIComponent(
          travelerId
        )}/seat-selection`,
        request
      );
    } catch (err) {
      throw new Error(getBackendErrorMessage(err));
    }
  }

  /** Best-effort clear after abandoned seat payment. */
  public async clearPendingSeatSelections(
    params: PendingSeatSave
  ): Promise<void> {
    // Every update recalculates the same reservation, so keep cleanup serialized too.
    for (const traveler of params.travelers) {
      await this.selectTravelerSeat(traveler.travelerId, {
        pnrCode: params.pnrCode,
        lastName: traveler.lastName,
        ...(traveler.departureSeatId !== undefined
          ? { departureSeatId: null }
          : {}),
        ...(traveler.returnSeatId !== undefined
          ? { returnSeatId: null }
          : {}),
      }).catch(() => undefined);
    }
  }

  // --- Invoices ---
  async uploadInvoice(reservationId: string, file: File): Promise<UploadedInvoice> {
    const fd = new FormData();
    fd.append("file", file);

    const res = await axios.post(
      `${INVOICES_BASE}/${reservationId}/invoices`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return res.data as UploadedInvoice;
  }

  async listInvoices(reservationId: string): Promise<UploadedInvoice[]> {
    const res = await axios.get(`${INVOICES_BASE}/${reservationId}/invoices`);
    return (res.data || []) as UploadedInvoice[];
  }

  async deleteInvoice(reservationId: string, invoiceId: string): Promise<void> {
    await axios.delete(`${INVOICES_BASE}/${reservationId}/invoices/${invoiceId}`);
  }
  async listPaymentReceipts(reservationId: string): Promise<PaymentReceipt[]> {
    const res = await axios.get(`${RESERVATIONS_BASE}/${reservationId}/payment-receipts`);
    return (res.data || []) as PaymentReceipt[];
  }

  async uploadPaymentReceipts(reservationId: string, files: File[]): Promise<PaymentReceipt[]> {
    const fd = new FormData();
    files.forEach((file) => fd.append("files", file));

    const res = await axios.post(
      `${RESERVATIONS_BASE}/${reservationId}/payment-receipts`,
      fd,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return (res.data || []) as PaymentReceipt[];
  }

  async getPaymentReceiptsStatus(reservationId: string): Promise<PaymentReceiptStatus> {
    const res = await axios.get(`${RESERVATIONS_BASE}/${reservationId}/payment-receipts/status`);
    return res.data as PaymentReceiptStatus;
  }
}

export default new ReservationsService();
